import { useMemo } from "react";
import { PIPELINE_COLUMNS, statusMeta, isOverdue } from "@/lib/statuses";
import { Card } from "@/components/ui/card";
import { Phone, CalendarClock, AlarmClock } from "lucide-react";

export const PipelineBoard = ({ prospects, onOpen }) => {
  const summary = useMemo(() => {
    const total = prospects.length;
    const called = prospects.filter((p) => p.status !== "a_appeler").length;
    const rdv = prospects.filter((p) => p.status === "rdv_pris").length;
    const late = prospects.filter((p) => isOverdue(p.next_action_at)).length;
    return {
      total, called, rdv, late,
      conversion: called ? Math.round((rdv / called) * 1000) / 10 : 0,
    };
  }, [prospects]);

  return (
    <div data-testid="pipeline-board">
      <Card className="p-5 sm:p-6 mb-6 bg-white border-brand-line rounded-md shadow-card">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
          {[
            { k: "Prospects", v: summary.total, id: "pipeline-total" },
            { k: "Appelés", v: summary.called, id: "pipeline-called" },
            { k: "RDV pris", v: summary.rdv, id: "pipeline-rdv", accent: true },
            { k: "Taux de conversion", v: `${summary.conversion}%`, id: "pipeline-conversion", accent: true },
            { k: "En retard", v: summary.late, id: "pipeline-late", late: true },
          ].map((s) => (
            <div key={s.k} data-testid={s.id}>
              <div className="eyebrow">{s.k}</div>
              <div className={`font-display text-2xl sm:text-3xl mt-2 leading-none ${
                s.late ? (summary.late ? "text-[#8A3F27]" : "text-brand-ink") : s.accent ? "text-brand-moss" : "text-brand-ink"
              }`}>{s.v}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
        {PIPELINE_COLUMNS.map((col) => {
          const items = prospects
            .filter((p) => col.statuses.includes(p.status || "a_appeler"))
            .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
          return (
            <div key={col.id} className="min-w-0" data-testid={`pipeline-col-${col.id}`}>
              <div className="flex items-baseline justify-between pb-3 mb-3 border-b border-brand-line">
                <span className="eyebrow">{col.label}</span>
                <span className="font-mono text-sm text-brand-ink">{items.length}</span>
              </div>
              <div className="space-y-2.5 max-h-[70vh] overflow-y-auto pr-1">
                {items.length === 0 && (
                  <div className="text-[11px] text-brand-subtle/70 py-6 text-center border border-dashed border-brand-line rounded-md">Vide</div>
                )}
                {items.map((p) => {
                  const meta = statusMeta(p.status);
                  const late = isOverdue(p.next_action_at);
                  return (
                    <button
                      key={p.siret}
                      onClick={() => onOpen(p)}
                      data-testid={`pipeline-card-${p.siret}`}
                      className={`block w-full text-left bg-white border rounded-md p-3 hover:shadow-card transition-colors ${
                        late ? "border-[#EBD5CB] hover:border-[#D8A695]" : "border-brand-line hover:border-brand-ink/25"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-semibold text-brand-ink line-clamp-2">{p.nom}</span>
                        <span className={`shrink-0 font-mono text-[10px] px-1.5 py-0.5 rounded border ${meta.cls}`}>{p.priority_score}</span>
                      </div>
                      <div className="mt-2 text-[10px] font-mono text-brand-subtle truncate">{p.ville} {p.code_postal ? `· ${p.code_postal}` : ""}</div>
                      {p.google_phone && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-mono text-brand-ink/80">
                          <Phone className="h-3 w-3 text-brand-moss" strokeWidth={1.75} /> {p.google_phone}
                        </div>
                      )}
                      {p.next_action_at && (
                        <div className={`mt-1.5 flex items-center gap-1.5 text-[10px] font-mono ${late ? "text-[#8A3F27]" : "text-brand-moss"}`}
                          data-testid={late ? `pipeline-overdue-${p.siret}` : undefined}>
                          {late
                            ? <AlarmClock className="h-3 w-3" strokeWidth={1.75} />
                            : <CalendarClock className="h-3 w-3" strokeWidth={1.75} />}
                          {new Date(p.next_action_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                          {late && " · en retard"}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
